import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CtaBand } from "../components/CtaBand";
import { BUSINESS } from "../config";

const SERVICES = [
  {
    id: "supply-and-installation",
    title: "Supply and Installation",
    text: "We provide the flooring materials and complete the installation from start to finish. You choose the colour, we handle the rest — subfloor check, installation, standard scotia and clean-up.",
  },
  {
    id: "supply-only",
    title: "Supply Only",
    text: `Customers can purchase hybrid flooring boards directly from us for their own project. Supply-only pricing starts from ${BUSINESS.supplyOnlyPrice}. Get in touch and we will confirm availability and pricing for your chosen colour.`,
  },
  {
    id: "installation-only",
    title: "Installation Only",
    text: "Customers who already have flooring materials can contact us for a professional installation quote. We will check your subfloor and existing surface first so there are no surprises on the day.",
  },
  {
    id: "subfloor-preparation",
    title: "Subfloor Preparation",
    text: "The existing floor must be inspected before installation. Additional levelling, preparation or repairs may be required if the surface is uneven or unsuitable. We will let you know upfront what is needed and what it will cost.",
  },
  {
    id: "scotia-installation",
    title: "Scotia Installation",
    text: "Matching scotia can be installed around the perimeter to create a clean and finished appearance. Standard scotia is included in our supply and install offer where applicable.",
  },
  {
    id: "flooring-removal-and-repairs",
    title: "Flooring Removal and Repairs",
    text: "Removal of existing flooring and minor flooring repairs can be quoted separately where required. Let us know what is currently down and we will include it in your written quote.",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader
        label="Our Services"
        title="Flooring supply and installation services"
        description="From full supply and installation through to subfloor preparation, scotia and repairs — Zum Flooring covers the whole job for homes across Melbourne."
        testId="services-header"
      />

      <section className="py-20 md:py-24" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => (
            <article
              key={s.id}
              className="border border-[#E5E5E5] rounded-xl p-8 bg-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1 fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`service-card-${s.id}`}
            >
              <span className="text-sm font-bold font-heading text-[#C87A38]/40">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="mt-2 text-2xl font-semibold font-heading">{s.title}</h2>
              <p className="mt-3 text-base text-[#525252] leading-relaxed">{s.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-20 md:py-24" data-testid="services-how-it-works">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C87A38]">How it works</p>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-semibold">Four simple steps to new floors</h2>
          <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              "Send us your flooring measurements, location and photos.",
              "Choose your preferred flooring colour.",
              "Receive a clear, written quote.",
              "Book a suitable installation date.",
            ].map((step, i) => (
              <li key={i} className="border border-[#E5E5E5] rounded-xl p-7 bg-white fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`services-step-${i}`}>
                <span className="text-4xl font-bold font-heading text-[#C87A38]/25">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-3 text-base font-medium leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
          <div className="mt-12">
            <Link
              to="/get-a-free-quote"
              className="inline-flex items-center gap-2 rounded-full bg-[#C87A38] px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-[#A6632B] focus:outline-none focus:ring-2 focus:ring-[#C87A38] focus:ring-offset-2"
              data-testid="services-quote-button"
            >
              Start with a free quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
